import React from "react";
import { useTranslation } from "react-i18next";
import "./ValuesSection.css";

/**
 * À propos — Section 03 : OPENlift, plateforme et passerelle matérielle.
 * Ajout autonome : aucun composant existant n'est modifié.
 * Images : ressources du site, dont la passerelle PNG spécifiée.
 * Les valeurs visibles sur l'aperçu OPENlift sont des exemples, pas des KPI vérifiés.
 */
const CONTENT = {
  fr: {
    eyebrow: "L'ÉCOSYSTÈME OPENlift",
    titleFirst: "Une solution.",
    titleAccent: "Deux composantes.",
    introduction:
      "OPENlift associe une plateforme de supervision et une passerelle matérielle 4G pour connecter les ascenseurs compatibles et faciliter le travail des équipes de maintenance.",
    products: [
      {
        id: "openlift",
        eyebrow: "SUPERVISION & MAINTENANCE CONNECTÉE",
        title: "Plateforme OPENlift",
        description:
          "Gardez une vision claire du parc et accédez aux données de fonctionnement pour faciliter le diagnostic à distance.",
        action: "Découvrir OPENlift",
        href: "/openlift",
        image: "/images/about/solutions/openlift-dashboard.webp",
        alt: "Aperçu du tableau de bord OPENlift et de son application mobile",
        mediaNote: "Aperçu produit · données illustratives"
      },
      {
        // Identifiant interne conservé pour préserver les classes CSS et le rendu validé.
        id: "iss",
        eyebrow: "PASSERELLE 4G MULTIMARQUE",
        title: "Passerelle OPENlift",
        description:
          "La passerelle 4G OPENlift connecte différents contrôleurs et transfère leurs données de façon fiable et sécurisée.",
        action: "Découvrir la passerelle OPENlift",
        href: "/openlift",
        image: "/images/about/solutions/passerelle-materiel.png",
        alt: "Passerelle matérielle 4G OPENlift pour les contrôleurs d'ascenseurs compatibles",
        mediaNote: "Passerelle 4G · selon compatibilité"
      }
    ]
  },
  en: {
    eyebrow: "THE OPENlift ECOSYSTEM",
    titleFirst: "One solution.",
    titleAccent: "Two components.",
    introduction:
      "OPENlift combines a supervision platform with a 4G hardware gateway to connect compatible elevators and support maintenance teams in the field.",
    products: [
      {
        id: "openlift",
        eyebrow: "CONNECTED SUPERVISION & MAINTENANCE",
        title: "OPENlift Platform",
        description:
          "Keep a clear overview of your fleet and access operating data to support remote fault investigation.",
        action: "Discover OPENlift",
        href: "/openlift",
        image: "/images/about/solutions/openlift-dashboard.webp",
        alt: "Preview of the OPENlift dashboard and mobile application",
        mediaNote: "Product preview · illustrative data"
      },
      {
        // Internal ID retained so existing CSS selectors and validated styling still apply.
        id: "iss",
        eyebrow: "MULTI-BRAND 4G GATEWAY",
        title: "OPENlift Gateway",
        description:
          "The OPENlift 4G gateway connects multiple types of controllers and sends data reliably and securely.",
        action: "Discover the OPENlift Gateway",
        href: "/openlift",
        image: "/images/about/solutions/passerelle-materiel.png",
        alt: "OPENlift 4G hardware gateway for compatible elevator controllers",
        mediaNote: "4G gateway · compatibility dependent"
      }
    ]
  }
};

function ProductCard({ product }) {
  return (
    <article className={`about-solutions__card about-solutions__card--${product.id}`}>
      <div className="about-solutions__card-copy">
        <p className="about-solutions__card-eyebrow">{product.eyebrow}</p>
        <h3 className="about-solutions__card-title">{product.title}</h3>
        <p className="about-solutions__card-description">{product.description}</p>
      </div>

      <div className="about-solutions__card-bottom">
        <a className="about-solutions__link" href={product.href}>
          <span>{product.action}</span>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="none">
            <path d="M4.5 12h14m-5.5-5.5 5.5 5.5-5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
        <figure className="about-solutions__figure">
          <img
            src={product.image}
            alt={product.alt}
            className="about-solutions__image"
            style={{ width: "116%", maxWidth: "none", height: "auto", marginLeft: "-8%" }}
            width={product.id === "openlift" ? 1400 : 1150}
            height={product.id === "openlift" ? 788 : 767}
            loading="lazy"
            decoding="async"
          />
          <figcaption className="about-solutions__media-note">{product.mediaNote}</figcaption>
        </figure>
      </div>
    </article>
  );
}

export default function AboutSolutions() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const copy = CONTENT[language] || CONTENT.fr;

  return (
    <section className="about-solutions" id="nos-solutions" aria-labelledby="about-solutions-title">
      <div className="about-solutions__container">
        <header className="about-solutions__header">
          <div className="about-solutions__header-title">
            <p className="about-solutions__eyebrow">{copy.eyebrow}</p>
            <h2 className="about-solutions__title" id="about-solutions-title">
              <span>{copy.titleFirst}</span>
              <span className="about-solutions__title-accent">{copy.titleAccent}</span>
            </h2>
          </div>
        </header>

        <div className="about-solutions__grid">
          {copy.products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}
